import * as THREE from 'three';
import { Model } from '../../hooks/useModelState';
import { PCNode } from '../../MineModel/Nodes';
import PCSpool from '../../MineModel/Spools/PCSpool';
import PCStope from '../../MineModel/Stopes/PCStope';
import { cadToThreeJsPosition } from '../scene-utils';
import { focusCameraOnPoint } from '../../MineModel/cameraFraming';

const ISOLATE_NODES_FILTER = 'bulkEditIsolateNodes';
const ISOLATE_SPOOLS_FILTER = 'bulkEditIsolateSpools';
const ISOLATE_STOPES_FILTER = 'bulkEditIsolateStopes';

export interface IsolateTargets {
  nodeIds: string[];
  spoolIds: string[];
  stopeIds: string[];
}

const collectTargetNodeIds = (model: Model, targets: IsolateTargets): Set<string> => {
  const ids = new Set<string>(targets.nodeIds);
  targets.spoolIds.forEach((spoolId) => {
    const spool = model.spools[spoolId];
    if (spool?.pointA?.id) ids.add(spool.pointA.id);
    if (spool?.pointB?.id) ids.add(spool.pointB.id);
  });
  const allStopes = Object.values(model.stopes).flat();
  allStopes.forEach((stope) => {
    if (stope?.data?.stopeId && targets.stopeIds.includes(stope.data.stopeId) && stope.originNode?.id) {
      ids.add(stope.originNode.id);
    }
  });
  return ids;
};

export const applyIsolate = (
  model: Model,
  targets: IsolateTargets,
  camera: THREE.OrthographicCamera | null,
  controls: any,
  modelTransform: { offset: THREE.Vector3; scale: number } | null,
) => {
  const targetNodeIdSet = collectTargetNodeIds(model, targets);
  const targetSpoolIdSet = new Set(targets.spoolIds);
  const targetStopeIdSet = new Set(targets.stopeIds);

  model.addFilter('nodes', ISOLATE_NODES_FILTER, (nodes: Record<string, PCNode>) => {
    const filtered: Record<string, PCNode> = {};
    Object.entries(nodes).forEach(([id, node]) => {
      if (targetNodeIdSet.has(id)) filtered[id] = node;
    });
    return filtered;
  });

  model.addFilter('spools', ISOLATE_SPOOLS_FILTER, (spools: Record<string, PCSpool>) => {
    const filtered: Record<string, PCSpool> = {};
    Object.entries(spools).forEach(([id, spool]) => {
      const keepBySpool = targetSpoolIdSet.has(id);
      const keepByNodes = targetNodeIdSet.has(spool.pointA?.id) && targetNodeIdSet.has(spool.pointB?.id);
      if (keepBySpool || keepByNodes) filtered[id] = spool;
    });
    return filtered;
  });

  model.addFilter('stopes', ISOLATE_STOPES_FILTER, (stopes: Record<string, PCStope[]>) => {
    const filtered: Record<string, PCStope[]> = {};
    Object.entries(stopes).forEach(([key, group]) => {
      const kept = group.filter((stope) => (
        stope?.data?.stopeId && targetStopeIdSet.has(stope.data.stopeId)
      ));
      if (kept.length > 0) filtered[key] = kept;
    });
    return filtered;
  });

  if (!camera || !controls || !modelTransform) return;

  const points: THREE.Vector3[] = [];
  targetNodeIdSet.forEach((id) => {
    const node = model.nodes[id];
    if (!node?.coords) return;
    const pos = cadToThreeJsPosition(node.coords).clone();
    pos.add(modelTransform.offset).multiplyScalar(modelTransform.scale);
    points.push(pos);
  });
  if (points.length === 0) return;

  const box = new THREE.Box3().setFromPoints(points);
  const sphere = new THREE.Sphere();
  box.getBoundingSphere(sphere);
  const radius = Math.max(sphere.radius, 1);
  focusCameraOnPoint(controls, camera, sphere.center, { radius, padding: 1.75 });
};

export const clearIsolate = (model: Model) => {
  model.removeFilter('nodes', ISOLATE_NODES_FILTER);
  model.removeFilter('spools', ISOLATE_SPOOLS_FILTER);
  model.removeFilter('stopes', ISOLATE_STOPES_FILTER);
};
